import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

export default function Login() {
  const [showPass, setShowPass] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: 'Bem-vindo(a) de volta!', description: 'Login realizado com sucesso.' });
      navigate('/painel');
    } catch (err) {
      toast({ title: 'Erro no login', description: err.response?.data?.detail || 'Credenciais inválidas.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-140px)] grid grid-cols-1 lg:grid-cols-2">
      <SEO title="Entrar" description="Acesse o painel do seu escritório de advocacia no Ambito Advogados, a plataforma de advogados do Âmbito Jurídico." />
      <div className="hidden lg:flex bg-[#f7fafb] border-r border-[#cbd8dc] text-[#122f36] p-12 items-center relative overflow-hidden">
        
        <div className="relative max-w-md">
          <div className="w-14 h-14 rounded-[10px] bg-[#edf4f5] border border-[#cbd8dc] flex items-center justify-center mb-6"><Scale className="w-7 h-7 text-[#153e44]" /></div>
          <h2 className="text-3xl font-semibold leading-tight">Bem-vindo(a) de volta ao Ambito Advogados, a plataforma de advogados do Âmbito Jurídico.</h2>
          <p className="mt-4 text-[#425a62] leading-relaxed">Gerencie seu perfil profissional, publique artigos, responda mensagens e conquiste novos clientes todos os dias.</p>
          <ul className="mt-8 space-y-3 text-sm text-[#354c54]">
            {['Painel administrativo completo', 'Estatísticas de visualizações do perfil', 'Publicação ilimitada de conteúdo', 'Contato direto via WhatsApp'].map(item => (
              <li key={item} className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#153e44]" /> {item}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <h1 className="text-2xl md:text-3xl font-semibold text-[#153e44]">Entrar na sua conta</h1>
          <p className="text-[#425a62] mt-2 text-sm">Acesse o painel do seu escritório de advocacia.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">E-mail</label>
              <div className="relative"><Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" /><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@escritorio.com.br" className="pl-9 h-11" /></div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Senha</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type={showPass ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 pr-10 h-11" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b858e] hover:text-[#354c54]">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold">{loading ? 'Entrando...' : 'Entrar'}</Button>
          </form>
          <p className="mt-8 text-center text-sm text-[#425a62]">Ainda não tem conta? <Link to="/cadastro" className="font-semibold text-[#153e44] hover:text-[#063f4c]">Cadastre seu escritório</Link></p>
        </div>
      </div>
    </div>
  );
}
